<template>
  <span v-if="canView" class="d-inline-block" :title="$t('core.change_history')">
    <o-button variant="outline-secondary" native-type="button" @click="open">
      <fa icon="history" />
    </o-button>

    <b-modal
      ref="modal"
      size="xl"
      scrollable
      :title="$t('core.change_history')"
      ok-only
      :ok-title="$t('core.close')"
      ok-variant="secondary"
    >
      <div v-if="loading" class="text-center py-5">
        <b-spinner />
      </div>

      <b-row v-else-if="tree" class="dh-modal-body no-gutters">
        <b-col md="5" class="dh-tree-col border-right">
          <b-form-checkbox v-model="hideUnchanged" class="dh-filter">
            {{ $t('core.hide_unchanged') }}
          </b-form-checkbox>
          <o-data-history-tree-node
            :node="tree"
            :level="0"
            :selected-key="selectedKey"
            :hide-unchanged="hideUnchanged"
            @select="selectNode"
          />
        </b-col>

        <b-col md="7" class="dh-changes-col">
          <div v-if="!selectedNode" class="text-muted text-center py-5">
            {{ $t('core.select_object_hint') }}
          </div>

          <div v-else>
            <h6 class="dh-changes-title">
              {{ selectedNode.label }}
              <small class="text-muted">· {{ selectedNode.type_label }}</small>
            </h6>

            <div v-if="changesLoading" class="text-center py-4">
              <b-spinner small />
            </div>

            <div v-else-if="sessions.length === 0" class="text-muted py-3">
              {{ $t('core.no_changes_recorded') }}
            </div>

            <b-card
              v-for="(session, i) in sessions"
              v-else
              :key="i"
              no-body
              class="mb-2 dh-session"
            >
              <div class="dh-session-header">
                <span>{{ session.created_at | formatDatetime }}</span>
                <span class="text-muted">{{ session.user ? session.user.name : '—' }}</span>
              </div>
              <b-table-simple small responsive class="mb-0 dh-session-table">
                <b-tbody>
                  <b-tr v-for="(change, j) in session.changes" :key="j">
                    <b-td class="dh-key">{{ change.key }}</b-td>
                    <b-td class="dh-old"><del>{{ formatValue(change.old_value) }}</del></b-td>
                    <b-td class="dh-arrow"><fa icon="arrow-right" /></b-td>
                    <b-td class="dh-new">{{ formatValue(change.new_value) }}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </b-card>
          </div>
        </b-col>
      </b-row>
    </b-modal>
  </span>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  name: 'o-data-history',

  props: {
    type: {
      type: String,
      required: true
    },
    id: {
      type: [Number, String],
      default: null
    }
  },

  data () {
    return {
      loading: false,
      tree: null,
      selectedNode: null,
      sessions: [],
      changesLoading: false,
      hideUnchanged: true
    }
  },

  computed: {
    ...mapGetters({ user: 'auth/user' }),
    canView () {
      return !!(this.user && this.user.is_super_user)
    },
    selectedKey () {
      return this.selectedNode ? this.selectedNode.type + '|' + this.selectedNode.id : null
    }
  },

  methods: {
    open () {
      this.$refs.modal.show()
      this.loadTree()
    },

    loadTree () {
      this.loading = true
      this.tree = null
      this.selectedNode = null
      this.sessions = []

      axios.get(`/api/system/data_changes/tree/${encodeURIComponent(this.type)}/${this.id}`)
        .then(({ data }) => {
          this.tree = data
          this.selectNode(data)
        })
        .finally(() => {
          this.loading = false
        })
    },

    selectNode (node) {
      this.selectedNode = node
      this.loadChanges(node)
    },

    loadChanges (node) {
      this.changesLoading = true
      this.sessions = []

      axios.get(`/api/system/data_changes/object/${encodeURIComponent(node.type)}/${node.id}`)
        .then(({ data }) => {
          this.sessions = data.sessions
        })
        .finally(() => {
          this.changesLoading = false
        })
    },

    formatValue (value) {
      if (value === null || value === undefined || value === '') {
        return '—'
      }
      return value
    }
  }
}
</script>

<style scoped>
.dh-modal-body {
  min-height: 55vh;
}
.dh-tree-col {
  max-height: 65vh;
  overflow: auto;
  padding-right: 8px;
  padding-left: 4px;
}
.dh-filter {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
  /* Only top/bottom padding — Bootstrap's .custom-control padding-left (1.5rem)
     must stay so the checkbox box is not clipped by the column's overflow. */
  padding-top: 2px;
  padding-bottom: 8px;
  margin-bottom: 4px;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.85rem;
}
.dh-changes-col {
  max-height: 65vh;
  overflow: auto;
  padding-left: 16px;
}
.dh-changes-title {
  position: sticky;
  top: 0;
  background: #fff;
  padding: 4px 0 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
  z-index: 1;
}
.dh-session {
  border: 1px solid #e9ecef;
}
.dh-session-header {
  display: flex;
  justify-content: space-between;
  padding: 6px 10px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.85rem;
}
.dh-session-table {
  font-size: 0.85rem;
}
.dh-key {
  font-weight: 600;
  width: 30%;
  word-break: break-word;
}
.dh-old {
  color: #c0392b;
  word-break: break-word;
}
.dh-arrow {
  width: 24px;
  text-align: center;
  color: #868e96;
}
.dh-new {
  color: #2e7d32;
  word-break: break-word;
}
</style>
